import { useState, useEffect } from 'react';
import { Text, Card, View, Icon } from '@smarterservices/smarter-ui';
import { getStore, useExamSessionStore } from '../../../stores/StoreContext';
import CONSTANTS from '../../../config/constants';

/**
 * Get calculator type display text
 * @param {string} virtualCalculatorType - The calculator type value
 * @returns {string} Display text for the calculator type
 */
const getCalculatorTypeText = (virtualCalculatorType) => {
  switch (virtualCalculatorType) {
    case 'four-function-calc':
      return 'Four Function';
    case 'scientific-calc':
      return 'Scientific';
    case 'matrix-calc':
      return 'Matrix';
    case 'graphing-calc':
      return 'Graphing';
    default:
      return null;
  }
};

/**
 * PermittedItem Component
 * 
 * Displays a single permitted item with icon, title, and optional notes.
 * 
 * @param {Object} props - Component props
 * @param {string} props.icon - Emoji icon for the item
 * @param {string} props.title - Item title
 * @param {string} [props.notes] - Optional notes/description
 * @returns {React.ReactElement} Formatted item display
 */
const PermittedItem = ({ title, notes }) => {
  return (
    <View as="div" justifyContent="center" alignItems="center">
      <View position="absolute" left="-10px">
        <Icon name="IconArrowBadgeRightFilled" size="x-small" />
      </View>
      <View margin="0 0 0 medium">
        <Text variant='contentImportant'>
          {title}
        </Text>
        {
          notes && (
            <Text
              color="secondary"
              size="small"
            >
              &nbsp; - {notes}
            </Text>
          )
        }
      </View>
    </View >
  );
};

const PermittedItems = (props) => {
  const examConfiguration = useExamSessionStore((state) => state.sessionData.examConfiguration);
  const [hasPermittedItems, setHasPermittedItems] = useState(false);
  const [calculatorInfo, setCalculatorInfo] = useState(null);

  useEffect(() => {
    if (!examConfiguration) return;
    setHasPermittedItems(
      examConfiguration.permittedBathroomBreak ||
      examConfiguration.permittedBlankPaper ||
      examConfiguration.permittedCalculator ||
      examConfiguration.permittedDictionary ||
      examConfiguration.permittedEarplugs ||
      examConfiguration.permittedFormulaSheet ||
      examConfiguration.permittedNotes ||
      examConfiguration.permittedTextbook ||
      examConfiguration.permittedWebsites ||
      examConfiguration.permittedOtherItems
    );

    if (examConfiguration.permittedCalculator) {
      if (examConfiguration.virtualCalculatorType) {
        if (examConfiguration.permittedCalculatorInformation) {
          setCalculatorInfo(`${examConfiguration.permittedCalculatorInformation} (Virtual option provided: ${getCalculatorTypeText(examConfiguration.virtualCalculatorType)})`);
        }
        else {
          setCalculatorInfo(`Virtual option provided: ${getCalculatorTypeText(examConfiguration.virtualCalculatorType)}`);
        }
      }
      else {
        setCalculatorInfo(examConfiguration.permittedCalculatorInformation);
      }
    }
    else {
      setCalculatorInfo(null);
    }
  }, [examConfiguration]);

  return (
    <Card {...props}>
      <Card.Header
        background="brand"
        padding={CONSTANTS.STYLES.CARD_HEADER_PADDING}
      >
        <Card.Title>
          <Text variant={CONSTANTS.STYLES.CARD_TITLE_VARIANT}>
            Permitted Items
          </Text>
        </Card.Title>
      </Card.Header>
      <Card.Body padding={CONSTANTS.STYLES.CARD_BODY_PADDING}>
        {examConfiguration.permittedBathroomBreak && (
          <PermittedItem
            title="Bathroom Break"
            notes={examConfiguration.permittedBathroomBreakInformation}
          />
        )}
        {examConfiguration.permittedBlankPaper && (
          <PermittedItem
            title="Blank Paper"
            notes={examConfiguration.permittedBlankPaperInformation}
          />
        )}
        {examConfiguration.permittedCalculator && (
          <PermittedItem
            title="Calculator"
            notes={calculatorInfo}
          />
        )}
        {examConfiguration.permittedDictionary && (
          <PermittedItem
            title="Dictionary"
            notes={examConfiguration.permittedDictionaryInformation}
          />
        )}
        {examConfiguration.permittedEarplugs && (
          <PermittedItem
            title="Earplugs"
            notes={examConfiguration.permittedEarplugsInformation}
          />
        )}
        {examConfiguration.permittedFormulaSheet && (
          <PermittedItem
            title="Formula Sheet"
            notes={examConfiguration.permittedFormulaSheetInformation}
          />
        )}
        {examConfiguration.permittedNotes && (
          <PermittedItem
            title="Notes"
            notes={examConfiguration.permittedNotesInformation}
          />
        )}
        {examConfiguration.permittedTextbook && (
          <PermittedItem
            title="Textbook"
            notes={examConfiguration.permittedTextbookInformation}
          />
        )}
        {examConfiguration.permittedWebsites && (
          <PermittedItem
            title="Websites"
            notes={examConfiguration.permittedWebsitesInformation}
          />
        )}
        {examConfiguration.permittedOtherItems && (
          <PermittedItem
            title="Other Items"
            notes={examConfiguration.permittedOtherItems}
          />
        )}

        {!hasPermittedItems && (
          <Text variant={CONSTANTS.STYLES.CARD_BODY_TEXT_VARIANT}>
            No additional items permitted
          </Text>
        )}

      </Card.Body>
    </Card>
  );
};

export default PermittedItems;
